import { createRef, h } from "preact";
import { useEffect, useLayoutEffect, useState } from "preact/hooks";
import { useSelector } from "react-redux";
import { CSSTransition } from "react-transition-group";

import BackButton from "./BackButton.jsx";
import Button from "./Button.jsx";
import Checkbox from "./questions/Checkbox.jsx";
import Choice from "./questions/Choice.jsx";
import Combobox from "./questions/Combobox.jsx";
import Date from "./questions/Date.jsx";
import FileUpload from "./questions/FileUpload.jsx";
import ImageUpload from "./questions/ImageUpload.jsx";
import FreeText from "./questions/FreeText.jsx";
import Info from "./questions/Info.jsx";
import LikertScale from "./questions/LikertScale.jsx";
import Money from "./questions/Money.jsx";
import Multiselect from "./questions/Multiselect.jsx";
import SkillChooser from "./questions/SkillChooser.jsx";
import Slider from "./questions/Slider.jsx";
import TextArea from "./questions/TextArea.jsx";
import InlineValidation from "./questions/InlineValidation.jsx";
import PlainCheckbox from "./Checkbox.jsx";

import * as validators from "../validators";
import i18n from "../i18n";
import { classNames, renderHtml, templateReplace } from "../helpers.js";
import { selectCurrentUser } from "../store/index";

const TEMPLATES = {
  "Elixir.Nioomi.Data.Boolean": Checkbox,
  "Elixir.Nioomi.Data.Combobox": Combobox,
  "Elixir.Nioomi.Data.Date": Date,
  "Elixir.Nioomi.Data.FileUpload": FileUpload,
  "Elixir.Nioomi.Data.FreeText": FreeText,
  "Elixir.Nioomi.Data.ImageUpload": ImageUpload,
  "Elixir.Nioomi.Data.Info": Info,
  "Elixir.Nioomi.Data.LikertScale": LikertScale,
  "Elixir.Nioomi.Data.Money": Money,
  "Elixir.Nioomi.Data.Multiselect": Multiselect,
  "Elixir.Nioomi.Data.Range": Slider,
  "Elixir.Nioomi.Data.Selection": Choice,
  "Elixir.Nioomi.Data.Skillset": SkillChooser,
  "Elixir.Nioomi.Data.Slider": Slider,
  "Elixir.Nioomi.Data.TextArea": TextArea,
};

function renderTemplate({ options, type }, props) {
  options = {
    ...options,
    helperText: options.helperText ? i18n.t(options.helperText) : undefined,
    label: options.label ? i18n.t(options.label) : undefined,
    subtitle: options.subtitle ? i18n.t(options.subtitle) : undefined,
  };
  if (type === "Elixir.Nioomi.Data.Range") {
    options.multiValue = true;
  }
  if (!TEMPLATES[type]) {
    throw new Error(`Template for type ${type} is not yet defined.`);
  }
  return h(TEMPLATES[type], { ...props, options });
}

function isValid({ options }, answer) {
  let validOrError = true;
  if (options.validation) {
    if (!validators[options.validation]) {
      throw new Error(
        `Validator ${options.validation} does not exist, please specify it`
      );
    }
    validOrError = validators[options.validation](value(answer));
  }
  return validOrError;
}

function value(answer) {
  if (Array.isArray(answer)) {
    return answer.length == 2 ? answer[1] : answer[0];
  }
  return answer;
}

export default function Question(props) {
  const alternative = props.question.template?.options?.alternative || [];
  const useAlternative = props.alternative;

  const [showValidationError, setShowValidationError] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [inlineValidationValid, setInlineValidationValid] = useState(true);
  const [answer, setAnswer] = useState(props.answer || "");
  const [skip, setSkip] = useState(false);
  const [validOrError, setValidOrError] = useState(true);
  const [lastKeyShown, setLastKeyShown] = useState(null);

  const currentUser = useSelector(selectCurrentUser);
  const ref = createRef();

  function handleSubmit(event) {
    event.preventDefault();
    let errorNow = false;
    if (skip) {
      return props.onSkip(event);
    }
    if (!answer && !props.optional && !useAlternative) {
      setValidationError(
        props.question.emptyError ||
          i18n.t("component.question.submit_not_answered")
      );
      errorNow = true;
    }
    if ((useAlternative || !validationError) && !errorNow) {
      props.onSubmit(event);
    } else {
      setShowValidationError(true);
    }
  }

  function handleSelectionChange(value, _event, validError) {
    setValidationError(validError || null);
    if (!validError) {
      setShowValidationError(false);
    }
    setAnswer(value);
    setValidOrError(isValid(props.question.template, value));
    props.onSelectionChange(value);
  }

  function handleAlternativeChange(event) {
    if (event.target.checked) {
      setValidationError(null);
      setShowValidationError(false);
      setAnswer("");
      setValidOrError(true);
      props.onSelectionChange("", true);
    } else {
      handleSelectionChange(ref.current.querySelector("input").value);
    }
  }

  function alternativeSelected() {
    return alternative.length && answer == alternative[0];
  }
  useEffect(() => {
    setValidationError(null);
    setShowValidationError(false);
  }, [props.question.key]);

  useEffect(() => {
    setAnswer(
      props.answer && props.answer.alternative
        ? alternative[0]
        : props.answer || ""
    );
  }, [props.answer || null]);

  useLayoutEffect(() => {
    const input = ref.current.querySelector(
      "input[type=text], textarea, input.focusable"
    );
    input && input.focus();
    setLastKeyShown(props.question.key);
    setShowValidationError(false);
  }, [props.question]);

  const { key, optional, placeholders, template } = props.question;
  // FIXME: That is definitely not a great way to extra control buttons here as well.
  let hideButtons = !currentUser && template.type === "file_upload" && !answer;
  const isButtonDisabled = useAlternative
    ? false
    : ((validOrError !== true || !answer) && !props.question.optional) ||
      (template.options?.hardInlineValidation && !inlineValidationValid);
  const subtitle = template.options?.subtitle
    ? renderHtml(
        templateReplace(i18n.t(template.options?.subtitle), placeholders)
      )
    : null;
  const title = templateReplace(i18n.t(props.question.title), placeholders);

  return (
    <CSSTransition
      appear
      in={lastKeyShown === key}
      timeout={500}
      classNames="slide-in-below"
    >
      <form onSubmit={handleSubmit} ref={ref} key={key}>
        <div
          class={classNames("question__header", subtitle ? "mb-40" : "mb-24")}
        >
          <div
            role="heading"
            aria-level="2"
            class={classNames("t-h6 t-h6--margin", subtitle && "pb-0")}
            data-qa="question-header"
          >
            {title}
          </div>
          {subtitle && (
            <p class="t-body3 mb-32 color-shades-charcoal">{subtitle}</p>
          )}
        </div>
        <div class="question__content mb-48 row" key={key}>
          <div class="col s12 m10 l12" key={key}>
            {renderTemplate(template, {
              answer: useAlternative ? "" : props.answer,
              dataKey: key,
              title: props.question.title,
              onSelect: handleSelectionChange,
              error:
                (showValidationError && validationError) ||
                (validOrError === true ? null : validOrError),
              disabled: useAlternative,
              errorBorder:
                template.options?.hardInlineValidation &&
                !inlineValidationValid &&
                props.answer,
            })}
            {!validationError && template.options?.inlineValidation && (
              <InlineValidation
                endpoint={template.options?.inlineValidation}
                value={props.answer}
                afterTextField
                hide={validOrError !== true}
                onChange={setInlineValidationValid}
              />
            )}
            {optional && optional !== true && (
              <p class="mt-32">
                <PlainCheckbox
                  name="skip"
                  onChange={(event) => {
                    setSkip(event.target.checked);
                  }}
                >
                  {i18n.t(optional)}
                </PlainCheckbox>
              </p>
            )}
            {alternative.length > 0 && (
              <PlainCheckbox
                class="mt-32"
                value={alternative[0]}
                onChange={handleAlternativeChange}
                checked={useAlternative}
              >
                {i18n.t(alternative[1])}
              </PlainCheckbox>
            )}
          </div>
        </div>
        <div class="question__button">
          <span>
            {props.onBack && (
              <BackButton
                class="mr-2"
                onClick={props.onBack}
                trackingParams={{ question: title }}
                noIndent
              />
            )}
            {!hideButtons && (
              <Button
                type="submit"
                disabled={isButtonDisabled}
                trackingParams={{ question: title }}
              >
                {i18n.t("actions.ok")}
              </Button>
            )}
            {props.skippable && (
              <Button
                class="pr-0"
                text
                onClick={props.onSkip}
                trackingParams={{ question: title }}
              >
                {i18n.t("actions.skip")}
              </Button>
            )}
          </span>
        </div>
      </form>
    </CSSTransition>
  );
}
